import { useState } from 'react';
import { motion } from 'framer-motion';
import { useConfig } from '../../context/ConfigContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const { FiSettings, FiImage, FiSave, FiUpload, FiEye, FiMusic, FiPlus, FiTrash2, FiEdit2, FiX, FiCheck, FiDollarSign } = FiIcons;

const ConfigurationSettings = () => {
    const { config, updateConfig, addReciter, removeReciter, updateReciter } = useConfig();
    const [platformSettings, setPlatformSettings] = useState({
        platformName: config.platformName || 'Islam 101',
        platformLogo: config.platformLogo || '/uploads/logo.png',
        tagline: config.tagline || 'Your comprehensive Islamic learning platform',
        contactEmail: config.contactEmail || 'contact@islam101.com',
        supportPhone: config.supportPhone || '+1 (555) 123-4567',
        address: config.address || '123 Islamic Center St, City, State 12345',
        donationLink: config.donationLink || '',
        socialLinks: config.socialLinks || {
            facebook: '',
            twitter: '',
            instagram: '',
            youtube: ''
        }
    });

    const [logoPreview, setLogoPreview] = useState(`${import.meta.env.VITE_API_URL}${platformSettings.platformLogo}`);
    const [showAddReciter, setShowAddReciter] = useState(false);
    const [newReciter, setNewReciter] = useState({ name: '', id: '' });
    const [editingReciterId, setEditingReciterId] = useState(null);
    const [editReciterData, setEditReciterData] = useState({ name: '', id: '' });

    const handleLogoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Check file size
        if (file.size > 2 * 1024 * 1024) { // 2MB limit
            toast.error('Logo file size should be less than 2MB');
            return;
        }

        // Show preview immediately
        const reader = new FileReader();
        reader.onload = (e) => {
            setLogoPreview(e.target.result);
        };
        reader.readAsDataURL(file);

        // Upload file to backend
        try {
            const formData = new FormData();
            formData.append('logo', file);

            const response = await api.post('/config/upload-logo', formData);

            if (response.data && response.data.url) {
                // Set the returned URL in platformSettings
                setPlatformSettings(prev => ({
                    ...prev,
                    platformLogo: response.data.url
                }));
                toast.success('Logo uploaded successfully');
            } else {
                toast.error('Failed to get logo from server');
            }

        } catch (error) {
            console.error(error);
            toast.error('Error uploading logo');
        }
    };

    const handleSaveSettings = () => {
        updateConfig(platformSettings);
        toast.success('Configuration updated successfully');
    };

    const handleSocialLinkChange = (platform, value) => {
        setPlatformSettings(prev => ({
            ...prev,
            socialLinks: {
                ...prev.socialLinks,
                [platform]: value
            }
        }));
    };

    const handleAddReciter = () => {
        if (!newReciter.name || !newReciter.id) {
            toast.error('Please fill in both name and ID');
            return;
        }
        addReciter(newReciter);
        setNewReciter({ name: '', id: '' });
        setShowAddReciter(false);
        toast.success('Reciter added successfully');
    };

    const handleUpdateReciter = () => {
        if (!editReciterData.name || !editReciterData.id) {
            toast.error('Please fill in both name and ID');
            return;
        }
        updateReciter(editingReciterId, editReciterData);
        setEditingReciterId(null);
        toast.success('Reciter updated successfully');
    };

    return (
        <div className="space-y-8">
            {/* Platform Branding */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
            >
                <div className="flex items-center space-x-3 mb-6">
                    <SafeIcon icon={FiSettings} className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Platform Branding
                    </h3>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Platform Name
                        </label>
                        <input
                            type="text"
                            value={platformSettings.platformName}
                            onChange={(e) => setPlatformSettings(prev => ({ ...prev, platformName: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="Enter platform name"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            This name will appear across all pages and in the browser title
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Tagline
                        </label>
                        <input
                            type="text"
                            value={platformSettings.tagline}
                            onChange={(e) => setPlatformSettings(prev => ({ ...prev, tagline: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="Enter platform tagline"
                        />
                    </div>
                </div>

                {/* Logo Upload */}
                <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Platform Logo
                    </label>
                    <div className="flex items-start space-x-6">
                        <div className="flex-shrink-0">
                            <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600">
                                {logoPreview ? (
                                    <img
                                        src={logoPreview}
                                        alt="Platform Logo"
                                        className="w-full h-full object-contain rounded-lg"
                                    />
                                ) : (
                                    <SafeIcon icon={FiImage} className="w-8 h-8 text-gray-400" />
                                )}
                            </div>
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center space-x-4">
                                <label className="cursor-pointer flex items-center space-x-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors">
                                    <SafeIcon icon={FiUpload} className="w-4 h-4" />
                                    <span>Upload Logo</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleLogoUpload}
                                        className="hidden"
                                    />
                                </label>
                                {logoPreview && (
                                    <button
                                        onClick={() => window.open(logoPreview, '_blank')}
                                        className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <SafeIcon icon={FiEye} className="w-4 h-4" />
                                        <span>Preview</span>
                                    </button>
                                )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                Recommended size: 120x120px. Max file size: 2MB. Supported formats: JPG, PNG, SVG
                            </p>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Donation Settings */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
            >
                <div className="flex items-center space-x-3 mb-6">
                    <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Donation Settings
                    </h3>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Donation Link (PayPal/Stripe)
                    </label>
                    <input
                        type="url"
                        value={platformSettings.donationLink}
                        onChange={(e) => setPlatformSettings(prev => ({ ...prev, donationLink: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="https://paypal.me/..."
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Leave empty to hide the Donate button on the Contact page.
                    </p>
                </div>
            </motion.div>


            {/* Reciters Management */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
            >
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <SafeIcon icon={FiMusic} className="w-5 h-5 text-emerald-600" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Custom Fields - Reciters
                        </h3>
                    </div>
                    <button
                        onClick={() => setShowAddReciter(true)}
                        className="flex items-center space-x-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm transition-colors"
                    >
                        <SafeIcon icon={FiPlus} className="w-4 h-4" />
                        <span>Add Reciter</span>
                    </button>
                </div>

                {showAddReciter && (
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4 border border-gray-200 dark:border-gray-600">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Add New Reciter</h4>
                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Name</label>
                                <input
                                    type="text"
                                    value={newReciter.name}
                                    onChange={(e) => setNewReciter(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="e.g. Mishary Rashid"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-500 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Audio ID (quranicaudio.com path)</label>
                                <input
                                    type="text"
                                    value={newReciter.id}
                                    onChange={(e) => setNewReciter(prev => ({ ...prev, id: e.target.value }))}
                                    placeholder="e.g. mishaari_raashid_al_3afaasee"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-500 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowAddReciter(false)}
                                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddReciter}
                                className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-md"
                            >
                                Save Reciter
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                            Note: Find Audio IDs at <a href="https://quranicaudio.com" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">quranicaudio.com</a>. The ID is the folder name in the URL.
                        </p>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-4 py-3">Reciter Name</th>
                                <th className="px-4 py-3">Audio ID</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(config?.reciters) ? (
                                config.reciters.map((reciter) => (
                                    <tr key={reciter.id} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                                        {editingReciterId === reciter.id ? (
                                        <input
                                            type="text"
                                            value={editReciterData.name}
                                            onChange={(e) => setEditReciterData(prev => ({ ...prev, name: e.target.value }))}
                                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-500 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                                        />
                                        ) : reciter.name}
                                    </td>
                                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                                        {editingReciterId === reciter.id ? (
                                        <input
                                            type="text"
                                            value={editReciterData.id}
                                            onChange={(e) => setEditReciterData(prev => ({ ...prev, id: e.target.value }))}
                                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-500 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                                        />
                                        ) : reciter.id}
                                    </td>
                                    <td className="px-4 py-3 text-right space-x-2">
                                        {editingReciterId === reciter.id ? (
                                        <>
                                            <button onClick={handleUpdateReciter} className="text-emerald-600 hover:text-emerald-800 p-1" title="Save">
                                            <SafeIcon icon={FiCheck} className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => setEditingReciterId(null)} className="text-gray-500 hover:text-gray-700 p-1" title="Cancel">
                                            <SafeIcon icon={FiX} className="w-4 h-4" />
                                            </button>
                                        </>
                                        ) : (
                                        <>
                                            <button
                                            onClick={() => {
                                                setEditingReciterId(reciter.id);
                                                setEditReciterData({ ...reciter });
                                            }}
                                            className="text-blue-600 hover:text-blue-800 p-1"
                                            title="Edit"
                                            >
                                            <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                                            </button>
                                            <button
                                            onClick={() => {
                                                if (window.confirm('Are you sure you want to delete this reciter?')) {
                                                removeReciter(reciter.id);
                                                }
                                            }}
                                            className="text-red-600 hover:text-red-800 p-1"
                                            title="Delete"
                                            >
                                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                                            </button>
                                        </>
                                        )}
                                    </td>
                                    </tr>
                                ))
                            ) : (
                                // Fallback UI
                                <tr>
                                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400" colSpan={3}>
                                    {config?.reciters == null
                                        ? 'Loading reciters…'
                                        : 'No reciters available or data format is invalid.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
            >
                <div className="flex items-center space-x-3 mb-6">
                    <SafeIcon icon={FiSettings} className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Contact Information
                    </h3>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Contact Email
                        </label>
                        <input
                            type="email"
                            value={platformSettings.contactEmail}
                            onChange={(e) => setPlatformSettings(prev => ({ ...prev, contactEmail: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="contact@islam101.com"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Support Phone
                        </label>
                        <input
                            type="tel"
                            value={platformSettings.supportPhone}
                            onChange={(e) => setPlatformSettings(prev => ({ ...prev, supportPhone: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="+1 (555) 123-4567"
                        />
                    </div>
                </div>

                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Address
                    </label>
                    <textarea
                        value={platformSettings.address}
                        onChange={(e) => setPlatformSettings(prev => ({ ...prev, address: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none"
                        placeholder="123 Islamic Center St, City, State 12345"
                    />
                </div>
            </motion.div>

            {/* Social Media Links */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
            >
                <div className="flex items-center space-x-3 mb-6">
                    <SafeIcon icon={FiSettings} className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Social Media Links
                    </h3>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Facebook
                        </label>
                        <input
                            type="url"
                            value={platformSettings.socialLinks.facebook}
                            onChange={(e) => handleSocialLinkChange('facebook', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="https://facebook.com/islam101"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Twitter
                        </label>
                        <input
                            type="url"
                            value={platformSettings.socialLinks.twitter}
                            onChange={(e) => handleSocialLinkChange('twitter', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="https://twitter.com/islam101"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Instagram
                        </label>
                        <input
                            type="url"
                            value={platformSettings.socialLinks.instagram}
                            onChange={(e) => handleSocialLinkChange('instagram', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="https://instagram.com/islam101"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            YouTube
                        </label>
                        <input
                            type="url"
                            value={platformSettings.socialLinks.youtube}
                            onChange={(e) => handleSocialLinkChange('youtube', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="https://youtube.com/islam101"
                        />
                    </div>
                </div>
            </motion.div>

            {/* Save Button */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex justify-end"
            >
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSaveSettings}
                    className="flex items-center space-x-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-colors"
                >
                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                    <span>Save Configuration</span>
                </motion.button>
            </motion.div>
        </div>
    );
};

export default ConfigurationSettings;